import { ArrowForward, DeleteOutline, EditOutlined, SaveOutlined } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { salesOrderLineType, trackingType } from '../types'
import './TrackingNumbers.css'
import { useState, useEffect } from 'react'
import { DatePicker, NumberField } from '../helper'
import dayjs from 'dayjs'
import axios from 'axios'

function TrackingNumbers(props: {
  order: {
    customerName: string
    jobName: string
    itemName: string
    salesOrderNumber: string
    salesOrderId: string
  }
  trackingNumbers: trackingType[]
  orderId: string
  lineId: string
  userType: string
  userId: string
  setOpen: (open: boolean) => void
  setParentTrackingNumbers: React.Dispatch<React.SetStateAction<trackingType[]>>
  handleAddTrackingNumber: (lineId: string, newTrackingNumber: trackingType) => Promise<void>
  handleEditTrackingNumbers: (
    lineId: string,
    trackingNumbers: (trackingType & { originalTrackingNumber: string })[],
  ) => Promise<void>
  onSalesOrderLinesUpdate?: (salesOrderLines: Partial<salesOrderLineType>[]) => void
}) {
  const [trackingNumbers, setTrackingNumbers] = useState<(trackingType & { editing: boolean })[]>(
    props.trackingNumbers.map(t => ({ ...t, editing: false })),
  )
  const [newTrackingNumbers, setNewTrackingNumbers] = useState<Set<string>>(new Set())
  const [newTrackingInput, setNewTrackingInput] = useState<string>('')
  const [newTrackingNumber, setNewTrackingNumber] = useState<trackingType>({
    tracking_number: '',
    ship_method: '',
    quantity_shipped: null,
    ship_date: null,
    delivery_date: null,
    person_signed: '',
  } as trackingType)
  const [selectedTrackingNumbers, setSelectedTrackingNumbers] = useState<trackingType[]>([])
  const [editingTrackingNumber, setEditingTrackingNumber] = useState<
    (trackingType & { originalTrackingNumber: string })[]
  >([])
  const [podFileNames, setPodFileNames] = useState<Map<string, string[]>>(new Map())
  const [orderId, setOrderId] = useState<string>('')
  const [fileLoading, setFileLoading] = useState<string>('loading')
  const [errorType, setErrorType] = useState<'duplicate' | 'general' | null>(null)
  const [deleteError, setDeleteError] = useState<{ show: boolean }>({ show: false })
  const [editError, setEditError] = useState<'ok' | 'error' | 'duplicate'>('ok')

  const handleAddMultipleTrackingNumbers = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const trackingNumbersToAdd = Array.from(newTrackingNumbers).map(
      trackingNumber =>
        ({
          tracking_number: trackingNumber.trim(),
          ship_method: newTrackingNumber.ship_method,
          quantity_shipped: newTrackingNumber.quantity_shipped,
          ship_date: newTrackingNumber.ship_date,
          delivery_date: newTrackingNumber.delivery_date,
          person_signed: newTrackingNumber.person_signed,
        }) as trackingType,
    )

    // Add each tracking number one by one
    const addPromises = trackingNumbersToAdd.map(trackingNumber =>
      props.handleAddTrackingNumber(props.lineId, trackingNumber),
    )

    Promise.all(addPromises)
      .then(() => {
        // Add to local state
        const newTrackingNumbersWithEditing = trackingNumbersToAdd.map(t => ({ ...t, editing: false }))
        setTrackingNumbers([...trackingNumbers, ...newTrackingNumbersWithEditing])

        // Clear the form
        setNewTrackingNumbers(new Set())
        setNewTrackingInput('')
        setNewTrackingNumber({
          tracking_number: '',
          ship_method: '',
          quantity_shipped: null,
          ship_date: null,
          delivery_date: null,
          person_signed: '',
        } as trackingType)
        setErrorType(null)
      })
      .catch(err => {
        if (err.message === 'Duplicate tracking number cannot be added.') {
          setErrorType('duplicate')
        } else {
          setErrorType('general')
        }
        console.error(err)
      })
  }

  const handleDeleteTrackingNumbers = () => {
    axios
      .delete(`/salesOrder/${props.lineId}/deleteTrackingNumber`, {
        data: {
          trackingNumbers: selectedTrackingNumbers,
          userId: props.userId,
          soNumber: props.order.salesOrderId,
        },
      })
      .then(() => {
        setDeleteError({ show: false })

        // Calculate remaining tracking numbers first
        const remainingTrackingNumbers = trackingNumbers.filter(t => !selectedTrackingNumbers.includes(t))

        // Update local state
        setTrackingNumbers(remainingTrackingNumbers)
        props.setParentTrackingNumbers(prevTrackingNumbers =>
          prevTrackingNumbers.filter(
            t =>
              !selectedTrackingNumbers.some(tn => tn.tracking_number === t.tracking_number) ||
              t.sales_order_line_txn_id !== props.lineId,
          ),
        )
        setSelectedTrackingNumbers([])

        // Update sales order lines locally
        if (props.onSalesOrderLinesUpdate) {
          const newTotalShippedQuantity = remainingTrackingNumbers.reduce(
            (sum, t) => sum + (t.quantity_shipped || 0),
            0,
          )

          props.onSalesOrderLinesUpdate([
            {
              sales_order_line_txn_id: props.lineId,
              total_shipped_quantity: newTotalShippedQuantity,
              tracking_number_exist_flag: remainingTrackingNumbers.length > 0,
            },
          ])
        }
      })
      .catch(err => {
        console.error(err)
        setDeleteError({ show: true })
      })
  }

  const getPodFileNames = async () => {
    setFileLoading('loading')
    try {
      const results = await Promise.all(
        trackingNumbers.map(async tracking => {
          const response = await axios
            .get(`/salesOrder/${props.order.salesOrderNumber}/${tracking.tracking_number}/getPod`)
            .catch(err => {
              console.error(err)
              return null
            })
          if (response?.data.Id) {
            setOrderId(response.data.Id)
            return [tracking.tracking_number, response.data.files]
          }
          return [tracking.tracking_number, []]
        }),
      )

      const newMap = new Map(podFileNames)
      results.forEach(([tracking_number, pod_file_names]) => {
        newMap.set(tracking_number, pod_file_names)
      })
      setPodFileNames(newMap)
    } catch (err) {
      console.error('Error in getPodFileNames:', err)
    } finally {
      setFileLoading('loaded')
    }
  }

  useEffect(() => {
    getPodFileNames()
  }, [trackingNumbers])

  const truncateMiddle = (str: string, maxLength: number = 15) => {
    if (str.length <= maxLength) return str
    const start = Math.ceil((maxLength - 3) / 2)
    const end = Math.floor((maxLength - 3) / 2)
    return `${str.slice(0, start)}...${str.slice(-end)}`
  }

  return (
    <div className="tracking-numbers-container">
      <div className="tracking-numbers-header">
        <div>
          <Typography variant="h6">Tracking Numbers</Typography>
          <IconButton onClick={() => props.setOpen(false)}>
            <ArrowForward />
          </IconButton>
        </div>
        <Divider />
      </div>
      <div className="tracking-numbers-content">
        <div className="tracking-numbers-order-info">
          <div>
            <Typography variant="body2" color="text.secondary">
              Customer Name:
            </Typography>
            <Typography variant="body1">{props.order.customerName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="text.secondary">
              Item Name:
            </Typography>
            <Typography variant="body1">{props.order.itemName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="text.secondary">
              Job Name:
            </Typography>
            <Typography variant="body1">{props.order.jobName}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="text.secondary">
              Sales Order Number:
            </Typography>
            <Typography variant="body1">{props.order.salesOrderNumber}</Typography>
          </div>
        </div>
        {props.userType !== 'Sales Rep' && props.userType !== 'Office Staff' && (
          <form className="tracking-numbers-add" onSubmit={handleAddMultipleTrackingNumbers}>
            <Typography variant="body1" width="100%">
              Add New Tracking Numbers:
            </Typography>
            <Grid container spacing="16px">
              <Grid size={4}>
                <FormControl>
                  <Autocomplete
                    options={[]}
                    value={Array.from(newTrackingNumbers)}
                    inputValue={newTrackingInput}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        label="Tracking Numbers *"
                        error={errorType !== null}
                        helperText={
                          errorType === 'duplicate'
                            ? 'Duplicate tracking #s cannot be added'
                            : 'Separate each value with a comma or space'
                        }
                      />
                    )}
                    multiple
                    freeSolo
                    onInputChange={(_, value) => {
                      const options = value.split(/[,| ]/g)
                      if (options.length > 1) {
                        setNewTrackingNumbers(
                          new Set(
                            Array.from(newTrackingNumbers)
                              .concat(options)
                              .map(x => x.trim())
                              .filter(x => x),
                          ),
                        )
                        setNewTrackingInput('')
                      } else {
                        setNewTrackingInput(value)
                      }
                    }}
                    onChange={(_, value) => {
                      setNewTrackingNumbers(new Set(value))
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Ship Method"
                  size="small"
                  name="shipMethod"
                  fullWidth
                  value={newTrackingNumber.ship_method}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, ship_method: e.target.value })}
                />
              </Grid>
              <Grid size={4}>
                <NumberField
                  label="Qty Shipped"
                  size="small"
                  fullWidth
                  value={newTrackingNumber.quantity_shipped}
                  onChange={value => setNewTrackingNumber({ ...newTrackingNumber, quantity_shipped: value })}
                />
              </Grid>
              <Grid size={4}>
                <DatePicker
                  label="Ship Date"
                  size="small"
                  value={newTrackingNumber.ship_date ? dayjs.utc(newTrackingNumber.ship_date) : null}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, ship_date: e?.toDate() ?? null })}
                />
              </Grid>
              <Grid size={4}>
                <DatePicker
                  label="Delivery Date"
                  size="small"
                  value={newTrackingNumber.delivery_date ? dayjs.utc(newTrackingNumber.delivery_date) : null}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, delivery_date: e?.toDate() ?? null })}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Person Signed"
                  size="small"
                  name="personSigned"
                  fullWidth
                  value={newTrackingNumber.person_signed}
                  onChange={e => setNewTrackingNumber({ ...newTrackingNumber, person_signed: e.target.value })}
                />
              </Grid>
            </Grid>
            <div className="tracking-numbers-add-actions">
              {errorType === 'general' && (
                <Alert severity="error">
                  An error occurred while adding the new tracking numbers. Please try again or contact support if the
                  error persists.
                </Alert>
              )}
              <Button variant="contained" size="large" disabled={newTrackingNumbers.size === 0} type="submit">
                Add
              </Button>
            </div>
          </form>
        )}
        <div className="tracking-numbers-table-container">
          {props.userType !== 'Office Staff' && (
            <div className="tracking-numbers-table-container-actions">
              <Typography variant="body1" color="text.secondary">
                {selectedTrackingNumbers.length} Selected
              </Typography>
              <div>
                <Button
                  variant="contained"
                  startIcon={<DeleteOutline />}
                  disabled={selectedTrackingNumbers.length === 0 || editingTrackingNumber.length > 0}
                  onClick={handleDeleteTrackingNumbers}
                >
                  Delete
                </Button>
                <Typography variant="body1" color="text.secondary">
                  or
                </Typography>
                <div className="tracking-numbers-edit-button-container">
                  <Button
                    variant="contained"
                    startIcon={editingTrackingNumber.length > 0 ? <SaveOutlined /> : <EditOutlined />}
                    disabled={selectedTrackingNumbers.length === 0 && editingTrackingNumber.length === 0}
                    onClick={() => {
                      if (editingTrackingNumber.length > 0) {
                        setEditError('ok')
                        props
                          .handleEditTrackingNumbers(props.lineId, editingTrackingNumber)
                          .then(() => {
                            setTrackingNumbers(
                              trackingNumbers.map(t => {
                                const index = editingTrackingNumber.findIndex(
                                  et => et.originalTrackingNumber === t.tracking_number,
                                )
                                if (index !== -1) {
                                  const { originalTrackingNumber: _, ...rest } = editingTrackingNumber[index]
                                  return { ...rest, editing: false }
                                }
                                return t
                              }),
                            )
                            setEditingTrackingNumber([])
                            setSelectedTrackingNumbers([])
                          })
                          .catch(err => {
                            if (
                              err.response?.status === 400 &&
                              err.response.data.includes('Duplicate tracking number')
                            ) {
                              setEditError('duplicate')
                            } else {
                              setEditError('error')
                            }
                            console.error(err)
                          })
                      } else {
                        const selectedForEditing = trackingNumbers
                          .filter(t => selectedTrackingNumbers.some(st => st.tracking_number === t.tracking_number))
                          .map(t => ({ ...t, originalTrackingNumber: t.tracking_number }))
                        setTrackingNumbers(
                          trackingNumbers.map(t =>
                            selectedTrackingNumbers.some(st => st.tracking_number === t.tracking_number)
                              ? { ...t, editing: true }
                              : t,
                          ),
                        )
                        setEditingTrackingNumber(selectedForEditing)
                      }
                    }}
                  >
                    {editingTrackingNumber.length > 0 ? 'Save' : 'Edit'}
                  </Button>
                  {(deleteError.show || editError !== 'ok') && (
                    <Alert severity="error">
                      {deleteError.show &&
                        'An error occurred while deleting tracking numbers. Please try again or contact support if the error persists.'}
                      {editError === 'duplicate' && 'All tracking numbers must be unique.'}
                      {editError === 'error' &&
                        'An error occurred while editing tracking numbers. Please try again or contact support if the error persists.'}
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}
          <TableContainer className="tracking-numbers-table">
            <Table>
              <TableHead className="tracking-numbers-table-header">
                <TableRow>
                  <TableCell width="42px">
                    <Checkbox
                      checked={trackingNumbers.length > 0 && selectedTrackingNumbers.length === trackingNumbers.length}
                      onChange={e => setSelectedTrackingNumbers(e.target.checked ? trackingNumbers : [])}
                      disabled={trackingNumbers.some(t => t.editing)}
                    />
                  </TableCell>
                  <TableCell>Tracking Number</TableCell>
                  <TableCell>Ship Method</TableCell>
                  <TableCell align="right">Qty Shipped</TableCell>
                  <TableCell align="right">Ship Date</TableCell>
                  <TableCell align="right">Delivery Date</TableCell>
                  <TableCell>Person Signed</TableCell>
                  <TableCell>Proof of Delivery</TableCell>
                  {props.userType !== 'Office Staff' && <TableCell width="40px" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {trackingNumbers.map(tracking => (
                  <TableRow key={tracking.tracking_number} className="tracking-numbers-table-row">
                    <TableCell>
                      <Checkbox
                        checked={selectedTrackingNumbers.some(t => t.tracking_number === tracking.tracking_number)}
                        onChange={e =>
                          setSelectedTrackingNumbers(
                            e.target.checked
                              ? [...selectedTrackingNumbers, tracking]
                              : selectedTrackingNumbers.filter(t => t.tracking_number !== tracking.tracking_number),
                          )
                        }
                        disabled={editingTrackingNumber.length > 0}
                      />
                    </TableCell>
                    {tracking.editing ? (
                      <>
                        <TableCell>
                          <TextField
                            size="small"
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.tracking_number ?? ''
                            }
                            onChange={e =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, tracking_number: e.target.value }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.ship_method ?? ''
                            }
                            onChange={e =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, ship_method: e.target.value }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <NumberField
                            label=""
                            size="small"
                            fullWidth
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.quantity_shipped
                            }
                            onChange={value =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, quantity_shipped: value }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            label=""
                            size="small"
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.ship_date
                                ? dayjs.utc(
                                    editingTrackingNumber.find(
                                      t => t.originalTrackingNumber === tracking.tracking_number,
                                    )?.ship_date,
                                  )
                                : null
                            }
                            onChange={e =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, ship_date: e?.toDate() ?? null }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            label=""
                            size="small"
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.delivery_date
                                ? dayjs.utc(
                                    editingTrackingNumber.find(
                                      t => t.originalTrackingNumber === tracking.tracking_number,
                                    )?.delivery_date,
                                  )
                                : null
                            }
                            onChange={e =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, delivery_date: e?.toDate() ?? null }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={
                              editingTrackingNumber.find(t => t.originalTrackingNumber === tracking.tracking_number)
                                ?.person_signed ?? ''
                            }
                            onChange={e =>
                              setEditingTrackingNumber(
                                editingTrackingNumber.map(t =>
                                  t.originalTrackingNumber === tracking.tracking_number
                                    ? { ...t, person_signed: e.target.value }
                                    : t,
                                ),
                              )
                            }
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{tracking.tracking_number}</TableCell>
                        <TableCell>{tracking.ship_method}</TableCell>
                        <TableCell align="right">{tracking.quantity_shipped}</TableCell>
                        <TableCell align="right">
                          {tracking.ship_date ? dayjs.utc(tracking.ship_date).format('MM/DD/YYYY') : ''}
                        </TableCell>
                        <TableCell align="right">
                          {tracking.delivery_date ? dayjs.utc(tracking.delivery_date).format('MM/DD/YYYY') : ''}
                        </TableCell>
                        <TableCell>{tracking.person_signed}</TableCell>
                      </>
                    )}
                    <TableCell>
                      <div className="tracking-numbers-pod">
                        <Typography className="tracking-numbers-pod-files" variant="body2" color="text.secondary">
                          <ul>
                            {podFileNames.get(tracking.tracking_number)?.length
                              ? podFileNames.get(tracking.tracking_number)?.map(name => (
                                  <Tooltip
                                    title={name}
                                    placement="top"
                                    arrow
                                    slotProps={{
                                      popper: { modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] },
                                    }}
                                  >
                                    <li key={name}>{truncateMiddle(name)}</li>
                                  </Tooltip>
                                ))
                              : fileLoading === 'loading'
                              ? 'Loading files...'
                              : !orderId
                              ? 'No Order ID'
                              : 'No files'}
                          </ul>
                        </Typography>
                        {orderId && (
                          <Button variant="contained" size="small" component="label">
                            Choose File
                            <input
                              type="file"
                              hidden
                              onChange={e => {
                                if (e.target.files) {
                                  const formData = new FormData()
                                  formData.append('podFile', e.target.files[0])
                                  axios
                                    .post(
                                      `/salesOrder/${props.order.salesOrderNumber}/${tracking.tracking_number}/uploadPOD`,
                                      formData,
                                      {
                                        headers: {
                                          'Content-Type': 'multipart/form-data',
                                        },
                                      },
                                    )
                                    .then(() => {
                                      setPodFileNames(prev => {
                                        const newMap = new Map(prev)
                                        const existingFiles = newMap.get(tracking.tracking_number) || []
                                        newMap.set(tracking.tracking_number, [
                                          ...existingFiles,
                                          e.target.files?.[0]?.name ?? '',
                                        ])
                                        return newMap
                                      })
                                    })
                                    .catch(err => {
                                      console.error('Error in file upload process:', err)
                                    })
                                }
                              }}
                            />
                          </Button>
                        )}
                        {orderId && (
                          <Button
                            variant="outlined"
                            size="small"
                            component="a"
                            href={`https://imprestechnology.sharepoint.com/sites/${process.env.REACT_APP_SHAREPOINT_SITE}/Order%20Portal%20Uploads/Forms/AllItems.aspx?id=%2Fsites%2FIT%2FOrder%20Portal%20Uploads%2F${orderId}%2F${props.order.salesOrderNumber}%2FPOD%2F${tracking.tracking_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Files
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    {props.userType !== 'Office Staff' && (
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            if (tracking.editing) {
                              const trackingToSave = editingTrackingNumber.find(
                                t => t.originalTrackingNumber === tracking.tracking_number,
                              )
                              if (trackingToSave) {
                                props.handleEditTrackingNumbers(props.lineId, [trackingToSave]).then(() => {
                                  setTrackingNumbers(
                                    trackingNumbers.map(t => {
                                      const { originalTrackingNumber: _, ...rest } = trackingToSave
                                      return t.tracking_number === tracking.tracking_number
                                        ? { ...rest, editing: false }
                                        : t
                                    }),
                                  )
                                  setEditingTrackingNumber(prev =>
                                    prev.filter(t => t.tracking_number !== tracking.tracking_number),
                                  )

                                  setSelectedTrackingNumbers(prev =>
                                    prev.filter(t => t.tracking_number !== tracking.tracking_number),
                                  )
                                })
                              }
                            } else {
                              setTrackingNumbers(
                                trackingNumbers.map(t =>
                                  t.tracking_number === tracking.tracking_number ? { ...t, editing: true } : t,
                                ),
                              )
                              setEditingTrackingNumber(prev => [
                                ...prev,
                                { ...tracking, originalTrackingNumber: tracking.tracking_number },
                              ])
                            }
                          }}
                        >
                          {tracking.editing ? <SaveOutlined /> : <EditOutlined />}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  )
}

export default TrackingNumbers
